/**
 * Empty state for "no grade ranges configured" on /orders/school.
 *
 * Per the Plan 11 spec, the CTA target depends on the viewer's
 * actual `Membership` rows — not just the `SCHOOL_ADMIN` profile
 * code. Three branches:
 *
 *   1. **Has at least one SCHOOL_ADMIN Membership** → "Configure your
 *      school settings" → `/settings/my-school`. Works as designed.
 *   2. **Has SCHOOL_ADMIN profile globally but zero school
 *      Memberships** → "You haven't been assigned to a school yet."
 *      with a "Contact support" Boombox-backed action. Routing them
 *      to `/settings/my-school` would land on a page with nothing
 *      to render (Plan 13 handles that gracefully but the experience
 *      is confusing).
 *   3. **Neither** → render nothing; the caller should fall through
 *      to whatever empty state matches the viewer's actual roles.
 *
 * Pattern generalizes: any empty-state CTA that points at a
 * destination should first verify the user has data at that
 * destination, or surface a different action.
 */

import { Button } from '@ordo/ui/button'
import { EmptyState } from '@ordo/ui/empty-state'
import { Layers, Mail } from 'lucide-react'
import type { ReactElement } from 'react'
import { Link } from 'react-router'

import { NO_SCHOOLS_EMPTY, SupportEmailButton } from '../../../lib/support'
import { SchoolSetupPendingEmptyState } from '../../school-setup-pending-empty-state'

export type EmptyStateAudience =
  | 'has-school-membership'
  | 'school-without-campus'
  | 'school-admin-without-school'
  | 'not-school-admin'

export interface GradeRangesEmptyStateProps {
  audience: EmptyStateAudience
  schoolName?: string | null
}

/**
 * Pure helper exported for unit testing — collapses the role +
 * membership signals into the audience the empty state should
 * address.
 *
 * - `hasSchoolAdminProfile` — `'SCHOOL_ADMIN'` in the user's profile
 *   codes (or an equivalent role like ORDO_ADMIN that grants the
 *   surface globally).
 * - `schoolMembershipCount` — count of SCHOOL_ADMIN Memberships
 *   resolved by the server (so we know whether routing to
 *   `/settings/my-school` will land on a page with data).
 */
export function resolveEmptyStateAudience(input: {
  hasSchoolAdminProfile: boolean
  schoolMembershipCount: number
  /** The user administers a school org, even if no campus is provisioned yet. */
  hasSchoolOrg?: boolean
}): EmptyStateAudience {
  if (input.schoolMembershipCount > 0) {
    return 'has-school-membership'
  }
  if (input.hasSchoolOrg) {
    return 'school-without-campus'
  }
  if (input.hasSchoolAdminProfile) {
    return 'school-admin-without-school'
  }
  return 'not-school-admin'
}

export function GradeRangesEmptyState({ audience, schoolName }: GradeRangesEmptyStateProps): ReactElement | null {
  if (audience === 'not-school-admin') {
    return null
  }
  if (audience === 'school-without-campus') {
    return <SchoolSetupPendingEmptyState surface="orders" schoolName={schoolName} />
  }
  if (audience === 'school-admin-without-school') {
    return (
      <EmptyState
        icon={<Mail className="h-10 w-10" />}
        title={NO_SCHOOLS_EMPTY.title}
        description={NO_SCHOOLS_EMPTY.description}
        action={<SupportEmailButton subject="No schools linked — Orders" />}
        padding="lg"
      />
    )
  }
  return (
    <EmptyState
      icon={<Layers className="h-10 w-10" />}
      title="No grade ranges configured"
      description="Set up the grade ranges this campus orders for in Settings — typically K-5, 6-8, and 9-12. Once configured, you'll see a calendar with a column per range."
      action={
        <Button asChild>
          <Link to="/settings/my-school">Configure your school settings</Link>
        </Button>
      }
      padding="lg"
    />
  )
}
